"use client";

import { useState, useRef, useMemo } from "react";
import dynamic from "next/dynamic";
import { Turnstile } from "@marsidev/react-turnstile";

const CodeEditor = dynamic(
  () => import("@uiw/react-textarea-code-editor").then((mod) => mod.default),
  { ssr: false }
);

interface FileTab {
  name: string;
  content: string;
}

interface RulePlaygroundProps {
  initialCode: string;
  initialQuery: string;
  language: string;
  command: string;
  height?: string;
}

interface ScanResult {
  rule_id: string;
  rule_name: string;
  message: string;
  severity: string;
  confidence: string;
  location: {
    file: string;
    line: number;
    snippet?: { start_line: number; end_line: number; lines: string[] };
  };
  detection: {
    type: string;
    confidence_score: number;
    source?: { file?: string; line: number; variable?: string };
    sink?: { file?: string; line: number; call?: string };
  };
  metadata?: { cwe?: string[] };
}

interface ScanResponse {
  success?: boolean;
  tool?: { name: string; version: string; url: string };
  scan?: { target: string; timestamp: string; duration: number; rules_executed: number };
  results?: ScanResult[];
  error?: string;
  summary?: { total: number; by_severity: Record<string, number>; by_detection_type?: Record<string, number> };
  rate_limit?: { remaining: number; reset_at: string };
}

/** Map a rule language to a Prism language identifier for the code editor. */
function getPrismLanguage(language: string): string {
  const map: Record<string, string> = {
    golang: 'go',
    'docker-compose': 'yaml',
    docker: 'docker',
  };
  return map[language] ?? language;
}

/** Map a specific filename to its Prism language identifier.
 *  Falls back to the rule-level language when no per-file override applies.
 */
function getFilePrismLanguage(filename: string, ruleLang: string): string {
  const base = filename.split('/').pop() ?? filename;
  if (base === 'go.mod') return 'go-module';
  if (base === 'go.sum') return 'bash'; // no Prism lang for checksums — bash gives neutral mono
  if (base.endsWith('.go')) return 'go';
  if (base.endsWith('.py')) return 'python';
  if (base === 'Dockerfile' || base.startsWith('Dockerfile.')) return 'docker';
  if (base.endsWith('.yaml') || base.endsWith('.yml')) return 'yaml';
  return getPrismLanguage(ruleLang);
}

function getDefaultFilename(language: string): string {
  switch (language) {
    case 'docker': return 'Dockerfile';
    case 'docker-compose': return 'docker-compose.yml';
    case 'golang': return 'main.go';
    default: return 'main.py';
  }
}

function parseFileTabs(code: string, language: string): FileTab[] {
  const separator = /^# --- file: (.+?) ---$/gm;
  const matches = [...code.matchAll(separator)];
  if (matches.length === 0) return [{ name: getDefaultFilename(language), content: code }];
  const files: FileTab[] = [];
  for (let i = 0; i < matches.length; i++) {
    const name = matches[i][1];
    const start = matches[i].index! + matches[i][0].length + 1;
    const end = i + 1 < matches.length ? matches[i + 1].index! : code.length;
    files.push({ name, content: code.slice(start, end).trim() });
  }
  return files;
}

export default function RulePlayground({ initialCode, initialQuery, language, command, height = "75vh" }: RulePlaygroundProps) {
  const initialFiles = useMemo(() => parseFileTabs(initialCode, language), [initialCode, language]);
  const [files, setFiles] = useState<FileTab[]>(initialFiles);
  const [activeTab, setActiveTab] = useState(0);
  const [query, setQuery] = useState(initialQuery);
  const [output, setOutput] = useState("");
  const [isRunning, setIsRunning] = useState(false);
  const [copiedCommand, setCopiedCommand] = useState(false);
  const [scanResults, setScanResults] = useState<ScanResult[]>([]);
  // highlightedLines computed live from scanResults via matchesFile() — no state needed
  const [showTurnstile, setShowTurnstile] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const codeContainerRef = useRef<HTMLDivElement>(null);
  const turnstileRef = useRef<any>(null);

  const activeFile = files[activeTab];
  // Match findings to files: server may use "user_code.py" while playground uses "main.py"
  // For single-file mode, all findings belong to the single file regardless of name
  const isSingleFile = files.length === 1;
  const matchesFile = (resultFile: string | undefined, tabName: string, tabIdx: number) => {
    if (!resultFile) return tabIdx === 0;
    if (resultFile === tabName) return true;
    if (isSingleFile && tabIdx === 0) return true; // Single file: all findings go here
    // Strip path prefix for comparison (server might return "project/user_code.py")
    const resultBase = resultFile.split("/").pop();
    const tabBase = tabName.split("/").pop();
    return resultBase === tabBase;
  };

  // Map server filenames (e.g., "user_code.py") to playground tab names (e.g., "main.py")
  const displayFileName = (serverFile: string | undefined) => {
    if (!serverFile) return files[0].name;
    // Find matching tab
    for (let i = 0; i < files.length; i++) {
      if (matchesFile(serverFile, files[i].name, i)) return files[i].name;
    }
    return serverFile;
  };

  // Compute highlighted lines with source/sink distinction for taint-flow rules
  // Source and sink may be in different files for cross-file analysis
  const { sinkLines, sourceLines, allHighlightedLines } = (() => {
    const sinks = new Set<number>();
    const sources = new Set<number>();
    for (const r of scanResults) {
      const hasFlow = r.detection.source && r.detection.sink;
      if (hasFlow) {
        // Check if source belongs to the active tab
        const sourceFile = r.detection.source!.file || r.location.file;
        if (matchesFile(sourceFile, activeFile.name, activeTab)) {
          sources.add(r.detection.source!.line);
        }
        // Check if sink belongs to the active tab
        const sinkFile = r.detection.sink!.file || r.location.file;
        if (matchesFile(sinkFile, activeFile.name, activeTab)) {
          sinks.add(r.detection.sink!.line);
        }
      } else if (matchesFile(r.location.file, activeFile.name, activeTab)) {
        // Pattern match or same-line flow — use location.line
        sinks.add(r.location.line);
      }
    }
    const all = new Set([...sinks, ...sources]);
    return { sinkLines: sinks, sourceLines: sources, allHighlightedLines: all };
  })();

  const activeFindings = scanResults.filter(
    (r) => matchesFile(r.location.file, activeFile.name, activeTab)
  );

  const issueCountPerFile = (fileName: string) => {
    const idx = files.findIndex((f) => f.name === fileName);
    let count = 0;
    for (const r of scanResults) {
      // Count if sink is in this file
      const sinkFile = r.detection.sink?.file || r.location.file;
      if (matchesFile(sinkFile, fileName, idx)) { count++; continue; }
      // Count if source is in this file (for cross-file flows)
      const sourceFile = r.detection.source?.file;
      if (sourceFile && matchesFile(sourceFile, fileName, idx)) { count++; }
    }
    return count;
  };

  const updateFileContent = (content: string) => {
    const updated = [...files];
    updated[activeTab] = { ...updated[activeTab], content };
    setFiles(updated);
  };

  const handleRun = async (captchaToken?: string) => {
    setIsRunning(true);
    setOutput("Running analysis...");
    setScanResults([]);

    try {
      const headers: Record<string, string> = { "Content-Type": "application/json" };
      const tokenToUse = captchaToken || turnstileToken;
      if (tokenToUse) headers["CF-Turnstile-Token"] = tokenToUse;

      const sourceCode =
        files.length === 1
          ? files[0].content
          : files.map((f) => `# --- file: ${f.name} ---\n${f.content}`).join("\n\n");

      const sandboxUrl = process.env.NEXT_PUBLIC_SANDBOX_URL
        || (typeof window !== "undefined" && window.location.hostname === "localhost"
          ? "http://localhost:8990"
          : "https://sandbox.codepathfinder.dev");
      const response = await fetch(`${sandboxUrl}/api/v1/scan`, {
        method: "POST",
        headers,
        body: JSON.stringify({ rule_code: query, source_code: sourceCode }),
      });

      if (response.status === 429) {
        setShowTurnstile(true);
        setOutput("Rate limit exceeded. Please complete the security challenge below.");
        setIsRunning(false);
        return;
      }
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

      const data: ScanResponse = await response.json();

      if (data.error?.toLowerCase().includes("rate limit") || (!data.success && data.rate_limit?.remaining === 0)) {
        setShowTurnstile(true);
        setOutput("Rate limit exceeded. Please complete the security challenge below.");
        setIsRunning(false);
        return;
      }
      if (data.error) { setOutput(`Error: ${data.error}`); setIsRunning(false); return; }

      if (data.success && data.results && data.results.length > 0) {
        setScanResults(data.results);

        // Switch to the tab containing the first finding
        const firstFile = data.results[0].location.file;
        if (firstFile && files.length > 1) {
          const idx = files.findIndex((f) => matchesFile(firstFile, f.name, files.indexOf(f)));
          if (idx >= 0 && idx !== activeTab) setActiveTab(idx);
        }

        setOutput(`Found ${data.results.length} potential security issue(s).`);
        setTurnstileToken(null);
      } else if (data.success || (data.summary && data.summary.total === 0)) {
        setScanResults([]);
        setOutput("No security issues found! The code appears to be secure.");
        setTurnstileToken(null);
      } else {
        setOutput("Scan completed but received unexpected response format.");
      }
    } catch (error) {
      setOutput(`Failed to connect to sandbox API.\n\nError: ${error instanceof Error ? error.message : "Unknown error"}`);
    } finally {
      setIsRunning(false);
    }
  };

  const handleCopyCommand = () => { navigator.clipboard.writeText(command); setCopiedCommand(true); setTimeout(() => setCopiedCommand(false), 2000); };
  const handleCopyCode = () => { navigator.clipboard.writeText(activeFile.content); };
  const handleCopyQuery = () => { navigator.clipboard.writeText(query); };
  const handleTurnstileSuccess = (token: string) => { setTurnstileToken(token); setShowTurnstile(false); handleRun(token); };

  const CopyIcon = () => (
    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
        d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
    </svg>
  );

  const LINE_HEIGHT_PX = 22.4; // 14px font * 1.6 line-height
  const TAB_BAR_PX = 44;       // tab bar height
  const EDITOR_PADDING_PX = 32; // 16px top + 16px bottom

  // Use the active file's line count so the editor fits each tab's content exactly
  const activeFileLines = activeFile.content.split("\n").length;
  const codeEditorHeight = Math.max(280, activeFileLines * LINE_HEIGHT_PX + EDITOR_PADDING_PX);
  const codeSectionHeight = codeEditorHeight + TAB_BAR_PX;

  const queryLines = query.split("\n").length;
  const ruleEditorHeight = Math.max(180, queryLines * LINE_HEIGHT_PX + EDITOR_PADDING_PX);
  const ruleSectionHeight = ruleEditorHeight + TAB_BAR_PX;

  const editorStyle = {
    backgroundColor: "#1e1e1e",
    color: "#d4d4d4",
    fontSize: "14px",
    lineHeight: "1.6",
    fontFamily: 'ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, "Liberation Mono", monospace',
  };

  return (
    <div className="space-y-6">
      {/* Command Bar */}
      <div className="bg-gray-900 rounded-lg p-4 flex items-center justify-between">
        <code className="text-lime-400 font-mono text-sm flex-1 truncate">{command}</code>
        <button onClick={handleCopyCommand}
          className="ml-4 px-4 py-2 bg-lime-400 text-gray-900 rounded-lg text-sm font-medium hover:bg-lime-300 transition-colors flex items-center gap-2 shrink-0">
          {copiedCommand ? (
            <><svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>Copied!</>
          ) : (<><CopyIcon /> Copy Command</>)}
        </button>
      </div>

      {/* Stacked Editor Container — dynamic height so all code is always visible */}
      <div className="border-2 border-gray-300 rounded-xl overflow-hidden bg-white shadow-lg relative flex flex-col">
        {/* Turnstile Overlay */}
        {showTurnstile && (
          <div className="absolute inset-0 bg-gray-900/80 backdrop-blur-sm flex items-center justify-center z-20">
            <div className="bg-white rounded-lg p-8 shadow-xl flex flex-col items-center gap-4 max-w-md">
              <div className="flex items-center justify-center gap-3 mb-2">
                <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
                <h3 className="text-xl font-bold text-gray-900">Security Verification Required</h3>
              </div>
              <p className="text-sm text-gray-600 text-center mb-4">Please complete the security challenge below to continue scanning.</p>
              <Turnstile ref={turnstileRef}
                siteKey={process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY || "1x00000000000000000000AA"}
                onSuccess={handleTurnstileSuccess}
                onError={() => { setOutput("Turnstile verification failed. Please try again."); setShowTurnstile(false); }}
                options={{ theme: "light", size: "normal" }} />
            </div>
          </div>
        )}

        {/* ===== TOP: Insecure Code (tabbed) ===== */}
        <div className="relative flex flex-col" style={{ height: `${codeSectionHeight}px` }}>
          {/* Tab Bar — VS Code style */}
          <div className="flex items-center justify-between overflow-x-auto shrink-0" style={{ backgroundColor: "#252526" }}>
            <div className="flex items-center min-w-0">
              {files.map((file, idx) => {
                const isActive = idx === activeTab;
                const count = issueCountPerFile(file.name);
                return (
                  <button key={idx} onClick={() => setActiveTab(idx)}
                    className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium cursor-pointer transition-all whitespace-nowrap border-r ${
                      isActive
                        ? "text-white border-r-transparent"
                        : "text-gray-400 hover:text-gray-200 border-r-gray-700"
                    }`}
                    style={{
                      backgroundColor: isActive ? "#1e1e1e" : "#2d2d2d",
                      borderTop: isActive ? "2px solid #86efac" : "2px solid transparent",
                    }}>
                    {/* Python file icon */}
                    <svg className="w-4 h-4 shrink-0" viewBox="0 0 24 24" fill="none">
                      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2z" fill={isActive ? "#3572A5" : "#555"} opacity="0.2"/>
                      <text x="6" y="16" fontSize="11" fontWeight="bold" fill={isActive ? "#3572A5" : "#888"} fontFamily="monospace">py</text>
                    </svg>
                    <span className={isActive ? "font-semibold" : ""}>{file.name}</span>
                    {count > 0 && (() => {
                      // Check if this file has sinks (red) or only sources (amber)
                      const hasSink = scanResults.some((r) => {
                        const sf = r.detection.sink?.file || r.location.file;
                        return matchesFile(sf, file.name, idx);
                      });
                      return <span className={`ml-1 px-1.5 py-0.5 ${hasSink ? "bg-red-500" : "bg-amber-500"} text-white text-xs font-bold rounded-full leading-none`}>{count}</span>;
                    })()}
                  </button>
                );
              })}
            </div>
            <button onClick={handleCopyCode} className="text-gray-400 hover:text-white text-xs font-medium flex items-center gap-1 px-3 py-1 shrink-0">
              <CopyIcon /> Copy
            </button>
          </div>

          {/* Loading Overlay */}
          {isRunning && (
            <div className="absolute inset-0 bg-gray-900/80 backdrop-blur-sm flex items-center justify-center z-10" style={{ top: "44px" }}>
              <div className="bg-white rounded-lg p-6 shadow-xl flex flex-col items-center gap-3">
                <svg className="w-12 h-12 animate-spin text-lime-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                <div className="text-center">
                  <div className="text-lg font-semibold text-gray-900">Analyzing Code...</div>
                  <div className="text-sm text-gray-600">{files.length > 1 ? "Running cross-file analysis" : "Running security scan"}</div>
                </div>
              </div>
            </div>
          )}

          {/* Code Editor with Line Numbers */}
          <div ref={codeContainerRef} className="flex" style={{ backgroundColor: "#1e1e1e", height: `${codeEditorHeight}px` }}>
            {/* Line Numbers */}
            <div className="flex flex-col text-right pr-2 pl-2 select-none" style={{ backgroundColor: "#1e1e1e", borderRight: "1px solid #333", paddingTop: "16px" }}>
              {activeFile.content.split("\n").map((_, idx) => {
                const ln = idx + 1;
                const isSink = sinkLines.has(ln);
                const isSource = sourceLines.has(ln);
                const hl = isSink || isSource;
                const finding = activeFindings.find((r) => r.location.line === ln);
                const label = isSource && !isSink ? "Source" : isSink && !isSource ? "Sink" : undefined;
                return (
                  <div key={idx}
                    className={`font-mono ${isSink ? "bg-red-500 text-white font-bold px-1 relative group" : isSource ? "bg-amber-500 text-white font-bold px-1 relative group" : "text-gray-400"}`}
                    style={{ lineHeight: "1.6", fontSize: "14px", height: "22.4px" }}>
                    {ln}
                    {hl && (
                      <div className="absolute left-full ml-2 top-0 hidden group-hover:block z-20 whitespace-nowrap">
                        <div className={`${isSink ? "bg-red-600" : "bg-amber-600"} text-white text-xs px-2 py-1 rounded shadow-lg flex items-center gap-1.5`}>
                          {label && <span className="font-bold uppercase text-[10px] opacity-80">{label}</span>}
                          {label && finding && <span className="opacity-50">|</span>}
                          {finding && <span>{finding.message}</span>}
                          {!finding && label && isSource && <span>Tainted data originates here</span>}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Editor */}
            <div className="flex-1 relative">
              <CodeEditor value={activeFile.content} language={getFilePrismLanguage(activeFile.name, language)}
                onChange={(e) => updateFileContent(e.target.value)}
                padding={16} className="font-mono text-sm w-full"
                aria-label={`Vulnerable code editor - ${activeFile.name}`}
                style={{ ...editorStyle, minHeight: `${codeEditorHeight}px` }} />
              {allHighlightedLines.size > 0 && (
                <div className="absolute top-0 left-0 right-0 pointer-events-none" style={{ paddingLeft: "16px", paddingTop: "16px", height: `${codeEditorHeight}px` }}>
                  {activeFile.content.split("\n").map((_, idx) => {
                    const ln = idx + 1;
                    const isSink = sinkLines.has(ln);
                    const isSource = sourceLines.has(ln);
                    if (isSink)
                      return <div key={idx} className="bg-red-500/20 border-l-2 border-red-500" style={{ height: "22.4px", marginLeft: "-16px", paddingLeft: "14px" }} />;
                    if (isSource)
                      return <div key={idx} className="bg-amber-500/20 border-l-2 border-amber-500" style={{ height: "22.4px", marginLeft: "-16px", paddingLeft: "14px" }} />;
                    return <div key={idx} style={{ height: "22.4px" }} />;
                  })}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* ===== BOTTOM: Rule ===== */}
        <div className="flex flex-col" style={{ height: `${ruleSectionHeight}px` }}>
          {/* Rule tab bar — matches file tabs style */}
          <div className="flex items-center justify-between shrink-0" style={{ backgroundColor: "#252526", borderTop: "1px solid #333" }}>
            <div className="flex items-center">
              <div
                className="flex items-center gap-2 px-4 py-2.5 text-sm font-semibold text-white"
                style={{ backgroundColor: "#1e1e1e", borderTop: "2px solid #86efac" }}>
                <svg className="w-4 h-4 shrink-0" fill="none" stroke="#86efac" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
                rule.py
              </div>
            </div>
            <button onClick={handleCopyQuery} className="text-gray-400 hover:text-white text-xs font-medium flex items-center gap-1 px-3 py-1">
              <CopyIcon /> Copy
            </button>
          </div>
          <div className="flex" style={{ backgroundColor: "#1e1e1e", height: `${ruleEditorHeight}px` }}>
            {/* Line Numbers */}
            <div className="flex flex-col text-right pr-2 pl-2 py-4 select-none flex-shrink-0" style={{ backgroundColor: "#1e1e1e", borderRight: "1px solid #333" }}>
              {query.split("\n").map((_, idx) => (
                <div key={idx} className="font-mono text-xs text-gray-400" style={{ lineHeight: "1.6", fontSize: "14px" }}>
                  {idx + 1}
                </div>
              ))}
            </div>
            {/* Editor */}
            <div className="flex-1">
              <CodeEditor value={query} language="python"
                onChange={(e) => setQuery(e.target.value)}
                padding={16} className="font-mono text-sm w-full"
                aria-label="Security rule editor"
                style={{ ...editorStyle, minHeight: `${ruleEditorHeight}px` }} />
            </div>
          </div>
        </div>

        {/* Run Button Bar — always visible */}
        <div className="bg-gray-50 border-t-2 border-gray-300 px-4 py-3 flex items-center justify-between shrink-0">
          <div className="text-xs text-gray-600">
            {files.length > 1 && (
              <span className="flex items-center gap-1.5">
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                </svg>
                Cross-file analysis: {files.length} files
              </span>
            )}
          </div>
          <button onClick={() => handleRun()} disabled={isRunning}
            className={`px-6 py-2.5 rounded-lg font-medium transition-all flex items-center gap-2 ${
              isRunning ? "bg-gray-400 text-white cursor-not-allowed" : "bg-lime-400 text-gray-900 hover:bg-lime-300 hover:shadow-md"
            }`}>
            {isRunning ? (
              <><svg className="w-4 h-4 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>Running...</>
            ) : (
              <><svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>Run Analysis</>
            )}
          </button>
        </div>
      </div>

      {/* Results */}
      {scanResults.length > 0 && (
        <div className="space-y-3">
          {/* Summary header */}
          <div className="flex items-center gap-3 px-1">
            <div className="flex items-center gap-2">
              <svg className="w-5 h-5 text-red-500" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 5a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 5zm0 9a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
              </svg>
              <span className="text-base font-semibold text-gray-900">{scanResults.length} Security {scanResults.length === 1 ? "Issue" : "Issues"} Found</span>
            </div>
          </div>

          {/* Finding cards */}
          {scanResults.map((result, idx) => {
            const hasFlow = result.detection.source && result.detection.sink
              && result.detection.source.line !== result.detection.sink.line;
            const rawSourceFile = result.detection.source?.file || result.location.file || files[0].name;
            const rawSinkFile = result.detection.sink?.file || result.location.file || files[0].name;
            const sourceFile = displayFileName(rawSourceFile);
            const sinkFile = displayFileName(rawSinkFile);
            const isCrossFile = hasFlow && sourceFile !== sinkFile;
            const severityColors: Record<string, string> = {
              critical: "bg-red-600 text-white",
              high: "bg-orange-500 text-white",
              medium: "bg-yellow-500 text-gray-900",
              low: "bg-blue-500 text-white",
            };
            const severityBg = severityColors[result.severity] || "bg-gray-500 text-white";
            const detectionLabel = result.detection.type?.includes("taint") ? "Taint Analysis" : "Pattern Match";

            return (
              <div key={idx} className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
                {/* Card header */}
                <div className="px-4 py-3 border-b border-gray-100 flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <span className={`px-2 py-0.5 text-xs font-bold rounded-md uppercase ${severityBg}`}>{result.severity}</span>
                      <span className="px-2 py-0.5 text-xs font-medium rounded-md bg-gray-100 text-gray-600 border border-gray-200">{detectionLabel}</span>
                      {result.metadata?.cwe?.map((cwe, i) => (
                        <a key={i} href={`https://cwe.mitre.org/data/definitions/${cwe.replace("CWE-", "")}.html`}
                          target="_blank" rel="noopener noreferrer"
                          className="px-2 py-0.5 text-xs font-mono rounded-md bg-gray-800 text-gray-300 hover:bg-gray-700 transition-colors">
                          {cwe}
                        </a>
                      ))}
                    </div>
                    <p className="text-sm text-gray-700 leading-relaxed">{result.message}</p>
                  </div>
                </div>

                {/* Taint flow visualization */}
                {hasFlow ? (
                  <div className="px-4 py-3 bg-gray-50">
                    <div className="flex items-stretch gap-0">
                      {/* Source */}
                      <button onClick={() => {
                        const ti = files.findIndex((f) => f.name === sourceFile || f.name.endsWith(sourceFile));
                        if (ti >= 0) setActiveTab(ti);
                      }} className="flex-1 text-left group">
                        <div className="border border-amber-300 bg-amber-50 rounded-lg p-3 group-hover:border-amber-400 transition-colors h-full">
                          <div className="flex items-center gap-1.5 mb-1.5">
                            <span className="w-2 h-2 rounded-full bg-amber-500" />
                            <span className="text-[10px] font-bold uppercase tracking-wider text-amber-700">Source</span>
                          </div>
                          <div className="font-mono text-xs text-gray-700">
                            <span className="text-amber-600 font-semibold">{sourceFile}:{result.detection.source!.line}</span>
                          </div>
                          {result.detection.source!.variable && (
                            <div className="mt-1 text-xs text-gray-500">var: <code className="bg-amber-100 px-1 rounded">{result.detection.source!.variable}</code></div>
                          )}
                        </div>
                      </button>

                      {/* Arrow */}
                      <div className="flex items-center px-2 shrink-0">
                        <div className="flex items-center gap-0.5 text-gray-400">
                          <div className="w-6 h-px bg-gray-300" />
                          <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z" clipRule="evenodd" />
                          </svg>
                        </div>
                      </div>

                      {/* Sink */}
                      <button onClick={() => {
                        const ti = files.findIndex((f) => f.name === sinkFile || f.name.endsWith(sinkFile));
                        if (ti >= 0) setActiveTab(ti);
                      }} className="flex-1 text-left group">
                        <div className="border border-red-300 bg-red-50 rounded-lg p-3 group-hover:border-red-400 transition-colors h-full">
                          <div className="flex items-center gap-1.5 mb-1.5">
                            <span className="w-2 h-2 rounded-full bg-red-500" />
                            <span className="text-[10px] font-bold uppercase tracking-wider text-red-700">Sink</span>
                          </div>
                          <div className="font-mono text-xs text-gray-700">
                            <span className="text-red-600 font-semibold">{sinkFile}:{result.detection.sink!.line}</span>
                          </div>
                          {result.detection.sink!.call && (
                            <div className="mt-1 text-xs text-gray-500">call: <code className="bg-red-100 px-1 rounded text-red-700">{result.detection.sink!.call.length > 50 ? result.detection.sink!.call.substring(0, 50) + "..." : result.detection.sink!.call}</code></div>
                          )}
                        </div>
                      </button>
                    </div>
                    {isCrossFile && (
                      <div className="mt-2 flex items-center gap-1.5 text-[10px] text-gray-400">
                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                        </svg>
                        <span>Cross-file data flow detected</span>
                      </div>
                    )}
                  </div>
                ) : (
                  /* Pattern match location */
                  <div className="px-4 py-2.5 bg-gray-50 flex items-center gap-2">
                    <button onClick={() => {
                      const fn = result.location.file || files[0].name;
                      const ti = files.findIndex((f) => f.name === fn);
                      if (ti >= 0) setActiveTab(ti);
                    }} className="flex items-center gap-2 text-xs font-mono text-gray-600 hover:text-gray-900 transition-colors">
                      <svg className="w-3.5 h-3.5 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                      <span className="text-red-600 font-semibold">{displayFileName(result.location.file)}:{result.location.line}</span>
                    </button>
                  </div>
                )}

                {/* Code snippet */}
                {result.location.snippet && (
                  <div className="border-t border-gray-100">
                    <div className="bg-gray-900 rounded-b-xl overflow-hidden">
                      <div className="px-4 py-2 flex items-center gap-2 text-[10px] text-gray-500 border-b border-gray-800">
                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
                        </svg>
                        <span>{displayFileName(result.location.file)}</span>
                      </div>
                      <pre className="px-4 py-2 text-xs font-mono overflow-x-auto">
                        {result.location.snippet.lines.map((line, i) => {
                          const lineNum = result.location.snippet!.start_line + i;
                          const isSinkLine = lineNum === result.location.line;
                          const isSourceLine = hasFlow && lineNum === result.detection.source!.line;
                          return (
                            <div key={i} className={`flex ${isSinkLine ? "bg-red-500/15" : isSourceLine ? "bg-amber-500/15" : ""}`}>
                              <span className={`select-none w-8 text-right pr-3 ${isSinkLine ? "text-red-400" : isSourceLine ? "text-amber-400" : "text-gray-600"}`}>{lineNum}</span>
                              <span className={isSinkLine ? "text-red-300" : isSourceLine ? "text-amber-300" : "text-gray-300"}>{line}</span>
                            </div>
                          );
                        })}
                      </pre>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {!isRunning && scanResults.length === 0 && output && !output.includes("Error") && !output.includes("Failed") && (
        <div className="bg-green-50 border-2 border-green-200 rounded-lg p-4">
          <div className="flex items-center gap-3">
            <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div>
              <h3 className="text-lg font-semibold text-green-900">No Issues Found</h3>
              <p className="text-sm text-green-700">The code appears to be secure according to this rule.</p>
            </div>
          </div>
        </div>
      )}

      {(output.includes("Error") || output.includes("Failed")) && !showTurnstile && (
        <div className="bg-yellow-50 border-2 border-yellow-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <svg className="w-6 h-6 text-yellow-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-yellow-900 mb-2">Connection Error</h3>
              <pre className="text-sm text-yellow-800 whitespace-pre-wrap font-mono">{output}</pre>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
